/**
 * OAuth Callback Page
 * 
 * Handles the redirect after Azure AD authentication
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@/components/Loader';
import { useAzureAuth } from '@/hooks/useAzureAuth';
import { useAuth } from '@chainlit/react-client';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { user, setUserFromAPI } = useAuth();
  const { isAuthenticated, accessToken, error } = useAzureAuth();
  const [status, setStatus] = useState<string>('Processing authentication...');

  useEffect(() => {
    const handleCallback = async () => {
      console.log('[AuthCallback] Handling OAuth callback...');

      if (error) {
        console.error('[AuthCallback] Authentication error:', error);
        setStatus(`Error: ${error}`);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      if (isAuthenticated && accessToken) {
        console.log('[AuthCallback] ✓ Azure authentication successful');
        setStatus('Completing sign-in...');
        
        // Fetch Chainlit user from backend
        if (!user) {
          await setUserFromAPI();
        }
      } else {
        console.log('[AuthCallback] Waiting for Azure authentication...');
        setStatus('Processing...');
      }
    };

    handleCallback();
  }, [isAuthenticated, accessToken, error, setUserFromAPI, user]);

  // Redirect to home when Chainlit user is ready
  useEffect(() => {
    if (user) {
      console.log('[AuthCallback] ✓ Chainlit user ready, redirecting to home');
      setStatus('Success! Redirecting...');
      setTimeout(() => {
        navigate('/');
      }, 500);
    }
  }, [user, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Loader />
        <p className="mt-4 text-lg">{status}</p>
      </div>
    </div>
  );
}
